import type { Transaction } from "@rocicorp/zero";
import { zql } from "./generatedDrizzleSchema";
import { generateId } from "./id";
import { insertRows, serverColumn, serverTable } from "./insertRows";

type ServerTx = Extract<Transaction, { location: "server" }>;

// TOOL_CALL_OUTPUT_V1_BACKCOMPAT start
type LegacyToolCallCopy = {
  source: { entryId: string; chatId: string };
  destination: { entryId: string; chatId: string };
};
// TOOL_CALL_OUTPUT_V1_BACKCOMPAT end

export type CopiedChatHistory = {
  entryCount: number;
  lastEntryId: string | null;
  // TOOL_CALL_OUTPUT_V1_BACKCOMPAT start
  legacyToolCallCopies: LegacyToolCallCopy[];
  // TOOL_CALL_OUTPUT_V1_BACKCOMPAT end
};

type Args = {
  tx: Transaction;
  sourceBranchId: string;
  newChatId: string;
  tenantId: string;
};

/** The body tables an entry can carry, keyed by the relation that reaches them. */
const ENTRY_BODIES = [
  {
    relation: "userMessage",
    table: "userMessages",
    columns: ["userId", "serviceAccountId", "text"],
  },
  {
    relation: "assistantMessage",
    table: "assistantMessages",
    columns: ["text"],
  },
  {
    relation: "toolCall",
    table: "toolCalls",
    columns: [
      "toolName",
      "anthropicId",
      "input",
      "output",
      "outputBlobId",
      "isError",
      "completedAt",
      "callerUserId",
    ],
  },
  {
    relation: "assistantReasoning",
    table: "assistantReasonings",
    columns: ["text", "signature"],
  },
  { relation: "systemMessage", table: "systemMessages", columns: ["text"] },
  {
    relation: "userAction",
    table: "userActions",
    columns: ["userId", "actionType", "metadata"],
  },
] as const;

/**
 * Copies every chat entry on `sourceBranchId`'s commits into `newChatId`,
 * preserving order and the `previousEntryId` chain. Returns the id of the last
 * entry written, so the caller can chain its own entries onto it.
 */
export async function copyChatHistory(args: Args): Promise<CopiedChatHistory> {
  return args.tx.location === "server"
    ? copyOnServer({ ...args, tx: args.tx })
    : copyOptimistically(args);
}

// Both paths read their entries differently but pair them with new ids, and
// report themselves, identically.
function planCopy(
  sources: Array<{ id: string; chatId: string; needsLegacyOutput: boolean }>,
  newChatId: string
) {
  const newIds = sources.map(() => generateId());
  return {
    oldIds: sources.map((source) => source.id),
    newIds,
    previousIds: newIds.map((_, i) => (i === 0 ? null : newIds[i - 1]!)),
    result: {
      entryCount: sources.length,
      lastEntryId: newIds.at(-1) ?? null,
      // TOOL_CALL_OUTPUT_V1_BACKCOMPAT start
      legacyToolCallCopies: sources.flatMap((source, i) =>
        source.needsLegacyOutput
          ? [
              {
                source: { entryId: source.id, chatId: source.chatId },
                destination: { entryId: newIds[i]!, chatId: newChatId },
              },
            ]
          : []
      ),
      // TOOL_CALL_OUTPUT_V1_BACKCOMPAT end
    },
  };
}

// Bodies stay inside Postgres: a tool call's output can be large, and pulling
// every one through the API only to send it back would dwarf the copy itself.
async function copyOnServer({
  tx,
  sourceBranchId,
  newChatId,
  tenantId,
}: Args & { tx: ServerTx }): Promise<CopiedChatHistory> {
  const query = (sql: string, params: unknown[]) =>
    tx.dbTransaction.query(sql, params);
  const entriesTable = serverTable("chatEntries");

  const entries = [
    ...(await query(
      `SELECT e.id, e.chat_id, tc.completed_at, tc.output_blob_id,
              tc.entry_id IS NOT NULL AS has_tool_call
       FROM ${entriesTable} e
       JOIN ${serverTable("chats")} ch ON ch.id = e.chat_id
       JOIN ${serverTable("workflowCommits")} c ON c.id = ch.commit_id
       LEFT JOIN ${serverTable("toolCalls")} tc ON tc.entry_id = e.id
       WHERE c.branch_id = $1
       ORDER BY e.created_at ASC, e.id ASC`,
      [sourceBranchId]
    )),
  ] as Array<{
    id: string;
    chat_id: string;
    completed_at: number | null;
    output_blob_id: string | null;
    has_tool_call: boolean;
  }>;

  const { oldIds, newIds, previousIds, result } = planCopy(
    entries.map((entry) => ({
      id: entry.id,
      chatId: entry.chat_id,
      needsLegacyOutput:
        entry.has_tool_call &&
        entry.completed_at !== null &&
        entry.output_blob_id === null,
    })),
    newChatId
  );
  if (entries.length === 0) return result;

  await query(
    `WITH m AS (
       SELECT * FROM unnest($1::text[], $2::text[], $3::text[]) AS t(old_id, new_id, prev_id)
     )
     INSERT INTO ${entriesTable} (id, chat_id, entry_type, created_at, previous_entry_id, tenant_id)
     SELECT m.new_id, $4, e.entry_type, e.created_at, m.prev_id, $5
     FROM m JOIN ${entriesTable} e ON e.id = m.old_id`,
    [oldIds, newIds, previousIds, newChatId, tenantId]
  );

  for (const { table, columns } of ENTRY_BODIES) {
    const names = columns.map((column) => serverColumn(table, column));
    await query(
      `WITH m AS (
         SELECT * FROM unnest($1::text[], $2::text[]) AS t(old_id, new_id)
       )
       INSERT INTO ${serverTable(table)} (entry_id, ${names.join(", ")}, tenant_id)
       SELECT m.new_id, ${names.map((n) => `s.${n}`).join(", ")}, $3
       FROM m JOIN ${serverTable(table)} s ON s.entry_id = m.old_id`,
      [oldIds, newIds, tenantId]
    );
  }

  return result;
}

async function copyOptimistically({
  tx,
  sourceBranchId,
  newChatId,
  tenantId,
}: Args): Promise<CopiedChatHistory> {
  const entries = (
    await tx.run(
      zql.workflowCommits
        .where("branchId", sourceBranchId)
        .related("chats", (c) =>
          c.related("entries", (e) =>
            e
              .related("userMessage")
              .related("assistantMessage")
              .related("toolCall")
              .related("assistantReasoning")
              .related("systemMessage")
              .related("userAction")
          )
        )
    )
  )
    .flatMap((commit) => commit.chats)
    .flatMap((chat) => chat.entries)
    // Same tiebreak as the server's ORDER BY, so entries written in one
    // mutation get the same chain here as in the authoritative copy.
    .sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));

  const { newIds, previousIds, result } = planCopy(
    entries.map((entry) => ({
      id: entry.id,
      chatId: entry.chatId,
      needsLegacyOutput:
        entry.toolCall?.completedAt != null &&
        entry.toolCall.outputBlobId === null,
    })),
    newChatId
  );

  await insertRows(
    tx,
    "chatEntries",
    entries.map((entry, i) => ({
      id: newIds[i]!,
      chatId: newChatId,
      entryType: entry.entryType,
      createdAt: entry.createdAt,
      previousEntryId: previousIds[i]!,
      tenantId,
    }))
  );

  for (const { relation, table, columns } of ENTRY_BODIES) {
    const rows = entries.flatMap((entry, i) => {
      const body = (entry as Record<string, unknown>)[relation] as
        | Record<string, unknown>
        | undefined;
      if (!body) return [];
      const copied = Object.fromEntries(columns.map((c) => [c, body[c]]));
      return [{ entryId: newIds[i]!, ...copied, tenantId }];
    });
    await insertRows(tx, table, rows as never);
  }

  return result;
}
